// Pure helpers for the global voice-message player: `navigator.mediaSession`
// metadata/seeking, and which player surface renders. Kept free of
// browser/React APIs so they're unit-testable; the hook in
// GlobalVoiceMessagePlayer.tsx turns these into MediaMetadata /
// setPositionState / action-handler calls.

/**
 * Which player surface renders, from the layout, the experimental setting and
 * whether anything is playing. Exactly one at a time — the strip *replaces*
 * the bar on the single-column layout, it never doubles it.
 *
 * - `'none'` — nothing playing.
 * - `'native'` — setting off: the re-parented `<audio>` element, at the bottom
 *   of the chat-list column (unchanged on every viewport).
 * - `'strip'` — single-column (small-screen) layout: that column is hidden
 *   while a chat is open, taking the bar with it (#137), so the player moves
 *   to a slim strip under the navbar instead.
 * - `'bar'` — the custom mini-player at the bottom of the chat-list column.
 */
export function voicePlayerSurface(
  smallScreen: boolean,
  customControls: boolean,
  hasSrc: boolean
): 'none' | 'native' | 'strip' | 'bar' {
  if (!hasSrc) {
    return 'none'
  }
  if (!customControls) {
    return 'native'
  }
  return smallScreen ? 'strip' : 'bar'
}

export type MediaSessionDetails = {
  senderName: string
  chatName: string
  /** Sender avatar URL (blob: works), or undefined when there is none. */
  avatarUrl?: string
}

/**
 * The metadata to publish to the OS media session.
 *
 * `suppressed` (the default) hides who/what is playing — only a generic
 * localized "Voice message" title, no artist, no artwork — because on some
 * systems the media session shows on the lock screen (forum 5423). When the
 * user opts in, the sender/chat/avatar are exposed.
 */
export function mediaSessionMetadata(
  suppressed: boolean,
  voiceMessageLabel: string,
  details: MediaSessionDetails
): { title: string; artist: string; artwork: { src: string }[] } {
  if (suppressed) {
    return { title: voiceMessageLabel, artist: '', artwork: [] }
  }
  return {
    title: details.senderName || voiceMessageLabel,
    artist: details.chatName,
    artwork: details.avatarUrl ? [{ src: details.avatarUrl }] : [],
  }
}

/**
 * New playback position after seeking `delta` seconds from `t`, clamped to
 * `[0, duration]`. A non-finite `duration` (stream / not-yet-known) only
 * clamps the lower bound.
 */
export function clampSeek(t: number, delta: number, duration: number): number {
  const next = t + delta
  if (next < 0) {
    return 0
  }
  if (Number.isFinite(duration) && next > duration) {
    return duration
  }
  return next
}
